import axios from 'axios';
import { getToken } from './auth';
import { invalidateProducts } from '../utils/productEventsHub';

import { API_URL } from './config';

const withAuth = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
  withCredentials: true,
});

// 📦 Products
export const getProducts = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/`, { params })).data;

export const getReferenceCosts = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/reference-costs`, withAuth())).data;

export const downloadPriceListPdf = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf`, {
    ...withAuth(),
    params,
    responseType: 'blob',
  })).data;

export const requestPriceListPdfJob = async (branchId, payload = {}) =>
  (await axios.post(`${API_URL}/branch/${branchId}/product/price-list/pdf/jobs`, payload, withAuth())).data;

export const getPriceListPdfJobStatus = async (branchId, jobId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf/jobs/${jobId}`, withAuth())).data;

export const downloadPriceListPdfJob = async (branchId, jobId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf/download/${jobId}`, {
    ...withAuth(),
    responseType: 'blob',
  })).data;

export const buildPublicPriceListPdfUrl = (branchId, jobId, { download = false } = {}) => {
  const url = new URL(`${API_URL}/branch/${branchId}/product/price-list/pdf/public/${jobId}`);
  if (download) url.searchParams.set('download', '1');
  return url.toString();
};

export const createProduct = async (branchId, product) => {
  product.productName = product.productName.trim().toLowerCase();
  product.productName = product.productName.charAt(0).toUpperCase() + product.productName.slice(1);
  const data = (await axios.post(`${API_URL}/branch/${branchId}/product`, product, withAuth())).data;
  invalidateProducts(branchId, { action: 'create-product', productId: data?.id, product: data });
  return data;
};

export const updateProduct = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-product',
    productId: id,
    product: response || { id, ...data },
  });
  return response;
};

export const deleteProduct = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-product', productId: id });
  return response;
};

// 🌱 Varieties
export const addVarietyToProduct = async (branchId, data) => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/variety`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'add-variety',
    varietyId: response?.id,
    productId: response?.productId ?? data?.productId,
    variety: response,
  });
  return response;
};

export const updateVariety = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-variety',
    varietyId: id,
    variety: response || { id, ...data },
  });
  return response;
};

export const deleteVariety = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/variety/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-variety', varietyId: id });
  return response;
};

const invalidateVarietyGroups = (branchId, productId, response = {}) => {
  invalidateProducts(branchId, {
    action: 'update-variety-groups',
    productId,
    varietyGroups: response?.groups || response?.VarietyGroups || [],
  });
};

export const getProductVarietyGroups = async (branchId, productId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/${productId}/variety-groups`, withAuth())).data;

export const createProductVarietyGroup = async (branchId, productId, payload = {}) => {
  const response = (await axios.post(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups`,
    payload,
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const updateProductVarietyGroup = async (branchId, productId, groupId, payload = {}) => {
  const response = (await axios.patch(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups/${groupId}`,
    payload,
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const deleteProductVarietyGroup = async (branchId, productId, groupId) => {
  const response = (await axios.delete(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups/${groupId}`,
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const reorderProductVarietyGroups = async (branchId, productId, groupIds = []) => {
  const response = (await axios.put(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups/order`,
    { groupIds },
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const replaceProductVarietyGroupVarieties = async (branchId, productId, groupId, varietyIds = []) => {
  const response = (await axios.put(
    `${API_URL}/branch/${branchId}/product/${productId}/variety-groups/${groupId}/varieties`,
    { varietyIds },
    withAuth()
  )).data;
  invalidateVarietyGroups(branchId, productId, response);
  return response;
};

export const markAsSold = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}/sold-out`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'variety-sold-out',
    varietyId: id,
    variety: response || { id, isSoldOut: true },
    isSoldOut: true,
  });
  return response;
};

export const markAsAvailable = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}/available`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'variety-available',
    varietyId: id,
    variety: response || { id, isSoldOut: false },
    isSoldOut: false,
  });
  return response;
};

export const markPriceAsSold = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}/sold-out`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'price-sold-out',
    priceId: id,
    price: response || { id, isSoldOut: true },
    isSoldOut: true,
  });
  return response;
};

export const markPriceAsAvailable = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}/available`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'price-available',
    priceId: id,
    price: response || { id, isSoldOut: false },
    isSoldOut: false,
  });
  return response;
};

// 💰 Variety Prices
export const addVarietyPriceToVariety = async (branchId, data) => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/variety-price`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'add-variety-price',
    priceId: response?.id,
    varietyId: response?.varietyId ?? data?.varietyId,
    price: response,
  });
  return response;
};

export const updateVarietyPrice = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-variety-price',
    priceId: id,
    price: response || { id, ...data },
  });
  return response;
};

export const removeVarietyPrice = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/variety-price/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-variety-price', priceId: id });
  return response;
};

export const getPriceByBarcode = async (branchId, barcode, clientType) => {
  const params = new URLSearchParams();
  if (barcode) params.set('barcode', barcode);
  if (clientType) params.set('clientType', clientType);
  const query = params.toString();
  const suffix = query ? `?${query}` : '';
  return (await axios.get(`${API_URL}/branch/${branchId}/product/prices/by-barcode${suffix}`)).data;
};
